import type { DiasTrabalhadosUsuario } from '@/services/api';
import { getFeriadosBrasilIntervalo } from '@/lib/feriadosBrasil';
import type { Periodo } from '@/lib/periodoFiltro';

/** Bits da máscara de cada dia — mesmos de `backend/src/services/userProfile/diasTrabalhados.ts`. */
export const SINAL = { acesso: 1, agenda: 2, acao: 4 } as const;

export type SinalFiltro = 'todos' | 'acesso' | 'agenda' | 'acao';

export const SINAIS_FILTRO: { key: SinalFiltro; label: string; descricao: string }[] = [
  { key: 'todos', label: 'Todos', descricao: 'Qualquer um dos sinais' },
  { key: 'acesso', label: 'App', descricao: 'Dia em que a pessoa acessou o Kora' },
  { key: 'agenda', label: 'Agenda', descricao: 'Dia com evento da pessoa na agenda (criou ou participa)' },
  { key: 'acao', label: 'Ação', descricao: 'Dia em que a pessoa criou ou editou algo em qualquer módulo' },
];

const MASCARA_FILTRO: Record<SinalFiltro, number> = {
  todos: SINAL.acesso | SINAL.agenda | SINAL.acao,
  acesso: SINAL.acesso,
  agenda: SINAL.agenda,
  acao: SINAL.acao,
};

/** Dias úteis seguidos sem registro a partir dos quais a pessoa é sinalizada. */
export const LIMITE_DIAS_SEM_REGISTRO = 5;

const proximoDia = (ymd: string) => {
  const d = new Date(`${ymd}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
};

// Feriados nacionais (sem os facultativos, como Carnaval) — mesma fonte do Calendário.
const cacheFeriados = new Map<number, Set<string>>();
function feriadosDoAno(ano: number): Set<string> {
  let set = cacheFeriados.get(ano);
  if (!set) {
    set = new Set(getFeriadosBrasilIntervalo(ano, ano).filter((f) => f.tipo === 'nacional').map((f) => f.data));
    cacheFeriados.set(ano, set);
  }
  return set;
}

/** Segunda a sexta, fora feriado nacional. */
export function ehDiaUtil(ymd: string): boolean {
  const semana = new Date(`${ymd}T12:00:00Z`).getUTCDay();
  if (semana === 0 || semana === 6) return false;
  return !feriadosDoAno(Number(ymd.slice(0, 4))).has(ymd);
}

/** Dias úteis em (depois, ate] — exclusivo no início, inclusivo no fim. */
function contarDiasUteisDepois(depois: string, ate: string): number {
  let n = 0;
  for (let dia = proximoDia(depois); dia <= ate; dia = proximoDia(dia)) {
    if (ehDiaUtil(dia)) n++;
  }
  return n;
}

const diaAnterior = (ymd: string) => {
  const d = new Date(`${ymd}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().slice(0, 10);
};

export interface ResumoDiasTrabalhados {
  /** Dias (qualquer dia da semana) com o sinal escolhido. */
  diasTrabalhados: number;
  /** Idem, por sinal. */
  porSinal: Record<Exclude<SinalFiltro, 'todos'>, number>; // acesso/agenda/acao
  /** Dias úteis no intervalo contado. */
  diasUteis: number;
  /** Dias úteis sem o sinal escolhido. */
  diasUteisSemSinal: number;
  /** Dias úteis seguidos sem acesso, ação registrada nem evento na agenda, até ontem (independe do período). */
  diasSemRegistro: number;
  sinalizado: boolean;
  ultimoRegistro: string | null;
  /** Intervalo efetivamente contado (YYYY-MM-DD). */
  inicio: string | null;
  fim: string;
}

/**
 * Consolida os dias de um usuário. Sem período, conta desde o cadastro ou o primeiro registro. O
 * alerta de 5 dias considera "registro" = acesso, ação no app ou evento na agenda (decisão 11b do
 * plano) e não conta o dia de hoje, que ainda não terminou.
 */
export function resumirDiasTrabalhados(
  u: DiasTrabalhadosUsuario | undefined,
  hoje: string,
  periodo: Periodo,
  sinal: SinalFiltro,
): ResumoDiasTrabalhados {
  // Sem período: desde o que vier primeiro, cadastro ou primeiro registro (base de teste tem
  // atividade anterior à data de cadastro, o que deixava os números incoerentes entre si).
  const primeiroDia = Object.keys(u?.dias ?? {}).sort()[0];
  const inicio = periodo.de ?? ([u?.criado_em, primeiroDia].filter(Boolean).sort()[0] as string | undefined) ?? null;
  const fim = periodo.ate && periodo.ate < hoje ? periodo.ate : hoje;
  const mascara = MASCARA_FILTRO[sinal];
  const porSinal = { acesso: 0, agenda: 0, acao: 0 };
  let diasTrabalhados = 0;
  let diasUteisComSinal = 0;

  for (const [dia, bits] of Object.entries(u?.dias ?? {})) {
    if ((inicio && dia < inicio) || dia > fim) continue;
    if (bits & SINAL.acesso) porSinal.acesso++;
    if (bits & SINAL.agenda) porSinal.agenda++;
    if (bits & SINAL.acao) porSinal.acao++;
    if (bits & mascara) {
      diasTrabalhados++;
      if (ehDiaUtil(dia)) diasUteisComSinal++;
    }
  }

  const diasUteis = inicio && inicio <= fim ? contarDiasUteisDepois(diaAnterior(inicio), fim) : 0;

  const ontem = diaAnterior(hoje);
  const base = u?.ultimo_registro ?? (u?.criado_em ? diaAnterior(u.criado_em) : null);
  const diasSemRegistro = base && base < ontem ? contarDiasUteisDepois(base, ontem) : 0;

  return {
    diasTrabalhados,
    porSinal,
    diasUteis,
    diasUteisSemSinal: Math.max(0, diasUteis - diasUteisComSinal),
    diasSemRegistro,
    sinalizado: diasSemRegistro >= LIMITE_DIAS_SEM_REGISTRO,
    ultimoRegistro: u?.ultimo_registro ?? null,
    inicio,
    fim,
  };
}

/** Segunda-feira da semana de um dia (YYYY-MM-DD). */
export function inicioDaSemana(ymd: string): string {
  let dia = ymd;
  while (new Date(`${dia}T12:00:00Z`).getUTCDay() !== 1) dia = diaAnterior(dia);
  return dia;
}

export interface SemanaDias {
  /** Segunda-feira da semana. */
  inicio: string;
  /** Dias com o sinal escolhido na semana (dentro do intervalo). */
  comSinal: number;
  /** Dias úteis da semana dentro do intervalo. */
  uteis: number;
}

/** Pessoa × semana: dias com sinal e dias úteis de cada semana (seg–dom) do intervalo. */
export function diasPorSemana(
  u: DiasTrabalhadosUsuario | undefined,
  inicio: string,
  fim: string,
  sinal: SinalFiltro,
): SemanaDias[] {
  const mascara = MASCARA_FILTRO[sinal];
  const semanas = new Map<string, SemanaDias>();
  for (let dia = inicio; dia <= fim; dia = proximoDia(dia)) {
    const chave = inicioDaSemana(dia);
    let s = semanas.get(chave);
    if (!s) {
      s = { inicio: chave, comSinal: 0, uteis: 0 };
      semanas.set(chave, s);
    }
    if (ehDiaUtil(dia)) s.uteis++;
    if ((u?.dias[dia] ?? 0) & mascara) s.comSinal++;
  }
  return [...semanas.values()];
}

/** Soma/subtrai dias de uma data YYYY-MM-DD. */
export function somarDias(ymd: string, n: number): string {
  const d = new Date(`${ymd}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}
